/**
 * The ARENA backdrop — recreated from the owner reference: a glowing teal play field
 * (a vertical gradient overlaid with a subtle blocky PIXEL-GRID) framed by a dark
 * carbon-steel border with angled corner cuts and a bright CYAN edge line, so the
 * game reads as a contained arena viewport rather than open space. Fully procedural
 * (no asset files), cheap, and purely decorative (no game rules / no determinism).
 *
 * `update(dt)` drifts the grid + breathes the cyan edge; `resize` reflows the field +
 * redraws the frame for a new viewport. Everything sits at deep negative depths so
 * hazards/player draw over it. Public API matches the old starfield Background, so
 * MainScene + StartFlowScene wire it the same way.
 */
import Phaser from 'phaser';

const TEX_FIELD = 'arena-field';
const TEX_GRID = 'arena-grid';

const DEPTH_FIELD = -100;
const DEPTH_GRID = -99;
const DEPTH_FRAME = -95;
const DEPTH_EDGE = -94;

/** Grid texture geometry (a tileable block of cells). */
const GRID_TEX = 256;
const GRID_CELL = 32;

/** Frame palette. */
const FRAME_DARK = 0x12171c;
const FRAME_DARK2 = 0x1d252b;
const FRAME_BEVEL = 0x3a474f;
const EDGE_CYAN = 0x4fe3f0;

export class Background {
  private readonly scene: Phaser.Scene;
  private field!: Phaser.GameObjects.Image;
  private grid!: Phaser.GameObjects.TileSprite;
  private frame!: Phaser.GameObjects.Graphics;
  private edge!: Phaser.GameObjects.Graphics;
  private t = 0;
  private w = 0;
  private h = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.ensureTextures();
    const { width, height } = scene.scale.gameSize;
    this.w = width;
    this.h = height;
    this.build();
  }

  /** Slow grid drift + a gentle pulse on the cyan edge. Call each frame. */
  update(dtSeconds: number): void {
    this.t += dtSeconds;
    // Subtle vertical drift of the pixel grid (energy-field shimmer).
    this.grid.tilePositionY -= dtSeconds * 6;
    this.grid.setAlpha(0.5 + 0.06 * Math.sin(this.t * 0.8));
    this.edge.setAlpha(0.8 + 0.2 * Math.sin(this.t * 1.4));
  }

  /** Reflow to a new viewport size (RESIZE scale mode). */
  resize(width: number, height: number): void {
    this.w = width;
    this.h = height;
    this.field.setPosition(width / 2, height / 2).setDisplaySize(width, height);
    this.grid.setPosition(width / 2, height / 2).setSize(width, height);
    this.drawFrame();
  }

  destroy(): void {
    this.field.destroy();
    this.grid.destroy();
    this.frame.destroy();
    this.edge.destroy();
  }

  /* --------------------------------------------------------------------- */

  private build(): void {
    const { w, h } = this;
    this.field = this.scene.add
      .image(w / 2, h / 2, TEX_FIELD)
      .setDepth(DEPTH_FIELD)
      .setDisplaySize(w, h);
    this.grid = this.scene.add
      .tileSprite(w / 2, h / 2, w, h, TEX_GRID)
      .setDepth(DEPTH_GRID)
      .setAlpha(0.5);
    this.frame = this.scene.add.graphics().setDepth(DEPTH_FRAME);
    this.edge = this.scene.add.graphics().setDepth(DEPTH_EDGE);
    this.drawFrame();
  }

  /** The play-field octagon (corner-cut rectangle) the dark frame surrounds. */
  private octagon(): Phaser.Math.Vector2[] {
    const { w, h } = this;
    const cut = Phaser.Math.Clamp(Math.min(w, h) * 0.15, 40, 150);
    const V = Phaser.Math.Vector2;
    return [
      new V(cut, 0),
      new V(w - cut, 0),
      new V(w, cut),
      new V(w, h - cut),
      new V(w - cut, h),
      new V(cut, h),
      new V(0, h - cut),
      new V(0, cut),
    ];
  }

  /** Redraw the dark corner frame + the glowing cyan edge for the current size. */
  private drawFrame(): void {
    const { w, h } = this;
    const cut = Phaser.Math.Clamp(Math.min(w, h) * 0.15, 40, 150);

    // Dark carbon-steel corner panels (the four diagonal cuts).
    this.frame.clear();
    const corner = (
      ax: number,
      ay: number,
      bx: number,
      by: number,
      cx: number,
      cy: number,
    ): void => {
      this.frame.fillStyle(FRAME_DARK, 1);
      this.frame.fillTriangle(ax, ay, bx, by, cx, cy);
      // A faint lighter bevel line along the diagonal cut.
      this.frame.lineStyle(2, FRAME_BEVEL, 0.7);
      this.frame.beginPath();
      this.frame.moveTo(bx, by);
      this.frame.lineTo(cx, cy);
      this.frame.strokePath();
    };
    corner(0, 0, cut, 0, 0, cut); // top-left
    corner(w, 0, w - cut, 0, w, cut); // top-right
    corner(w, h, w - cut, h, w, h - cut); // bottom-right
    corner(0, h, cut, h, 0, h - cut); // bottom-left

    // A subtle carbon hatch on the corner panels (a couple of diagonal streaks).
    this.frame.lineStyle(3, FRAME_DARK2, 0.6);
    const hatch = (ox: number, oy: number, dirX: number, dirY: number): void => {
      for (let i = 1; i <= 3; i++) {
        const o = i * (cut / 4);
        this.frame.beginPath();
        this.frame.moveTo(ox + dirX * o, oy);
        this.frame.lineTo(ox, oy + dirY * o);
        this.frame.strokePath();
      }
    };
    hatch(0, 0, 1, 1);
    hatch(w, 0, -1, 1);
    hatch(w, h, -1, -1);
    hatch(0, h, 1, -1);

    // The glowing cyan edge tracing the play-field octagon: a soft outer glow line
    // and a crisp bright inner line.
    const poly = this.octagon();
    this.edge.clear();
    this.edge.lineStyle(6, EDGE_CYAN, 0.18);
    this.edge.strokePoints(poly, true);
    this.edge.lineStyle(2, EDGE_CYAN, 0.95);
    this.edge.strokePoints(poly, true);
  }

  /* ---- textures (generated once per scene) ---- */

  private ensureTextures(): void {
    const tx = this.scene.textures;

    // Vertical teal gradient play field (brighter teal top → deep teal bottom).
    if (!tx.exists(TEX_FIELD)) {
      const ww = 8;
      const hh = 256;
      const g = this.scene.add.graphics();
      const top = { r: 0x2c, g: 0x93, b: 0xa6 };
      const bot = { r: 0x0b, g: 0x32, b: 0x3e };
      for (let y = 0; y < hh; y++) {
        const f = y / (hh - 1);
        const r = Math.round(top.r + (bot.r - top.r) * f);
        const gg = Math.round(top.g + (bot.g - top.g) * f);
        const b = Math.round(top.b + (bot.b - top.b) * f);
        g.fillStyle((r << 16) | (gg << 8) | b, 1);
        g.fillRect(0, y, ww, 1);
      }
      g.generateTexture(TEX_FIELD, ww, hh);
      g.destroy();
    }

    // Tileable pixel grid: cells of varied teal/cyan brightness (and a few darker
    // cells) on a transparent field, so it overlays the gradient as a data-grid.
    if (!tx.exists(TEX_GRID)) {
      const g = this.scene.add.graphics();
      const n = GRID_TEX / GRID_CELL;
      for (let cy = 0; cy < n; cy++) {
        for (let cx = 0; cx < n; cx++) {
          const r = Math.random();
          let color = 0x000000;
          let alpha = 0;
          if (r < 0.34) {
            // empty cell
          } else if (r < 0.7) {
            color = 0x5fd6e0;
            alpha = 0.08;
          } else if (r < 0.86) {
            color = 0x8af0ec;
            alpha = 0.16;
          } else {
            color = 0x07242c; // darker cell
            alpha = 0.16;
          }
          if (alpha > 0) {
            g.fillStyle(color, alpha);
            // inset a touch so faint cell gutters read as a grid.
            g.fillRect(cx * GRID_CELL + 1, cy * GRID_CELL + 1, GRID_CELL - 2, GRID_CELL - 2);
          }
        }
      }
      g.generateTexture(TEX_GRID, GRID_TEX, GRID_TEX);
      g.destroy();
    }
  }
}
